import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/outline';

interface BenchmarkData {
  comparison: {
    btcReturn: number;
    ethReturn: number;
    sp500Return: number;
    outperformance: number;
  };
  benchmarkReturns: Record<string, number>;
  availableBenchmarks: string[];
}

interface BenchmarkComparisonProps {
  benchmarkData?: BenchmarkData;
  portfolioReturn: number;
}

const BenchmarkComparison: React.FC<BenchmarkComparisonProps> = ({ 
  benchmarkData, 
  portfolioReturn 
}) => {
  // Add comprehensive null checks for the nested data structure
  if (!benchmarkData || !benchmarkData.comparison) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-sm text-gray-600 space-y-2">
            <p>📊 No portfolio data yet</p>
            <p className="text-xs">Start trading or paper trading to see benchmark comparisons</p>
          </div>
        </div>
      </div>
    );
  }

  // Destructure comparison data safely with fallbacks
  const {
    btcReturn = 0,
    ethReturn = 0,
    sp500Return = 0,
    outperformance = 0
  } = benchmarkData.comparison || {};

  const formatPercentage = (value: number) => `${value.toFixed(2)}%`;
  const formatDifference = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  const getPerformanceIcon = (difference: number) => {
    if (difference > 0.5) {
      return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
    } else if (difference < -0.5) {
      return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
    }
    return <MinusIcon className="h-4 w-4 text-gray-500" />;
  };

  const getPerformanceColor = (difference: number) => {
    if (difference > 0.5) return 'text-green-600';
    if (difference < -0.5) return 'text-red-600';
    return 'text-gray-600';
  };

  const getPerformanceBg = (difference: number) => {
    if (difference > 0.5) return 'bg-green-50 border-green-200';
    if (difference < -0.5) return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };

  // Benchmark data for comparison using safely destructured values
  const benchmarks = [
    {
      name: 'Bitcoin',
      symbol: 'BTC',
      return: btcReturn,
      difference: portfolioReturn - btcReturn,
      description: 'Leading cryptocurrency',
      color: '#f59e0b'
    },
    {
      name: 'Ethereum',
      symbol: 'ETH',
      return: ethReturn,
      difference: portfolioReturn - ethReturn,
      description: 'Smart contract platform',
      color: '#8b5cf6'
    },
    {
      name: 'S&P 500',
      symbol: 'SPY',
      return: sp500Return,
      difference: portfolioReturn - sp500Return,
      description: 'US equity index',
      color: '#10b981'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Portfolio vs Benchmarks Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {formatPercentage(portfolioReturn)}
          </div>
          <div className="text-sm text-gray-600">Portfolio Return</div>
        </div>
        <div className="text-center">
                      <div className={`text-2xl font-bold ${getPerformanceColor(outperformance)}`}>
              {formatDifference(outperformance)}
          </div>
          <div className="text-sm text-gray-600">vs Avg Benchmark</div>
        </div>
      </div>

      {/* Individual Benchmark Comparisons */}
      <div className="space-y-3">
        {benchmarks.map((benchmark) => (
          <div key={benchmark.symbol} className={`border rounded-lg p-4 ${getPerformanceBg(benchmark.difference)}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: benchmark.color }}
                ></div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900">{benchmark.name}</h4>
                  <p className="text-xs text-gray-500">{benchmark.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getPerformanceIcon(benchmark.difference)}
                <span className={`text-sm font-medium ${getPerformanceColor(benchmark.difference)}`}>
                  {formatDifference(benchmark.difference)}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="font-medium text-gray-900">
                  {formatPercentage(portfolioReturn)}
                </div>
                <div className="text-xs text-gray-500">Portfolio</div>
              </div>
              <div>
                <div className="font-medium text-gray-900">
                  {formatPercentage(benchmark.return)}
                </div>
                <div className="text-xs text-gray-500">{benchmark.symbol}</div>
              </div>
              <div>
                <div className={`font-medium ${getPerformanceColor(benchmark.difference)}`}>
                  {formatDifference(benchmark.difference)}
                </div>
                <div className="text-xs text-gray-500">Difference</div>
              </div>
            </div>

            {/* Performance Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Underperform</span>
                <span>Outperform</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-0.5 h-4 bg-gray-400"></div>
                </div>
                <div 
                  className={`h-2 rounded-full ${
                    benchmark.difference > 0 ? 'bg-green-500' : benchmark.difference < 0 ? 'bg-red-500' : 'bg-gray-400'
                  }`}
                  style={{
                    width: `${Math.min(Math.abs(benchmark.difference) * 2, 50)}%`,
                    marginLeft: benchmark.difference < 0 ? `${50 - Math.min(Math.abs(benchmark.difference) * 2, 50)}%` : '50%'
                  }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Ranking */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Performance Ranking</h4>
        <div className="space-y-2">
          {[
            { name: 'Portfolio', return: portfolioReturn, isPortfolio: true },
            ...benchmarks.map(b => ({ name: b.name, return: b.return, isPortfolio: false }))
          ]
            .sort((a, b) => b.return - a.return)
            .map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium ${
                    item.isPortfolio 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {index + 1}
                  </div>
                  <span className={`text-sm ${
                    item.isPortfolio ? 'font-medium text-blue-900' : 'text-gray-700'
                  }`}>
                    {item.name}
                  </span>
                </div>
                <span className={`text-sm font-medium ${
                  item.isPortfolio ? 'text-blue-900' : 'text-gray-900'
                }`}>
                  {formatPercentage(item.return)}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Risk-Adjusted Comparison (Simplified) */}
      <div className="border rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Risk-Return Profile</h4>
        <div className="text-center space-y-2">
          <div className="text-xs text-gray-500 mb-3">
            Higher risk assets typically show higher volatility
          </div>
          
          {/* Risk indicators */}
          <div className="flex justify-center space-x-6">
            <div className="text-center">
              <div className="w-3 h-3 bg-blue-600 rounded-full mx-auto mb-1"></div>
              <div className="text-xs text-gray-600">Portfolio</div>
              <div className="text-xs text-gray-500">Balanced</div>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mx-auto mb-1"></div>
              <div className="text-xs text-gray-600">BTC</div>
              <div className="text-xs text-gray-500">High Risk</div>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mx-auto mb-1"></div>
              <div className="text-xs text-gray-600">ETH</div>
              <div className="text-xs text-gray-500">High Risk</div>
            </div>
            <div className="text-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-1"></div>
              <div className="text-xs text-gray-600">S&P 500</div>
              <div className="text-xs text-gray-500">Low Risk</div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Key Insights</h4>
        <ul className="space-y-1 text-xs text-blue-800">
          {outperformance > 2 && (
            <li>• Strong outperformance vs benchmarks (+{formatPercentage(outperformance)})</li>
          )}
          {outperformance < -2 && (
            <li>• Underperforming vs benchmarks ({formatPercentage(outperformance)})</li>
          )}
          {benchmarks.filter(b => b.difference > 0).length > 0 && (
            <li>• Outperforming {benchmarks.filter(b => b.difference > 0).length} out of {benchmarks.length} benchmarks</li>
          )}
          {Math.abs(outperformance) <= 2 && (
            <li>• Portfolio performance is closely aligned with market benchmarks</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default BenchmarkComparison; 